/**
 * Cliente HTTP tipado consumido pelas páginas do shell. // ux-neutrality:allow doc-comment
 *
 * Camadas: // ux-neutrality:allow doc-comment
 *   - leitura de baseURL a partir de VITE_API_BASE_URL (fallback "/api"). // ux-neutrality:allow doc-comment
 *   - proxy do Vite em dev e o Caddy em produção apontam para o backend. // ux-neutrality:allow doc-comment
 *   - timeout default. // ux-neutrality:allow doc-comment
 *   - desde E2.F1: interceptor de auth que anexa Authorization Bearer // ux-neutrality:allow doc-comment
 *     quando há token no store de sessão, e converte 401 em // ux-neutrality:allow doc-comment
 *     `clearToken()` + navegação para /login (fechando o ciclo do JWT // ux-neutrality:allow doc-comment
 *     vencido). // ux-neutrality:allow doc-comment
 */
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useMemo } from 'react';
import { useSessionStore } from '@/stores/session';

export interface ApiClientConfig {
  baseURL: string;
  timeoutMs: number;
}

/**
 * Owner, 03/11 (lentidão da POC): com 15 s, a tela dizia "não foi possível" em ações que o
 * servidor concluía logo depois (exclusão, recorte, revisões, releitura, avaliação), quando a
 * requisição esperava a vez atrás da preparação do caso. As ações longas seguem com o teto próprio.
 */
export const DEFAULT_TIMEOUT_MS = 60_000;
export const DEFAULT_BASE_URL = '/api';
export const HTTP_UNAUTHORIZED = 401;
export const HTTP_FORBIDDEN = 403;
/** Detalhe do 403 que significa "o contexto guardado não é mais seu" (E57.F2.C03). */
export const BANCO_ATIVO_INVALIDO = 'BANCO_ATIVO_INVALIDO';

export function resolveBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) {
    return fromEnv;
  }
  return DEFAULT_BASE_URL;
}

export function createApiClient(config?: Partial<ApiClientConfig>): AxiosInstance {
  return axios.create({
    baseURL: config?.baseURL ?? resolveBaseUrl(),
    timeout: config?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    headers: { Accept: 'application/json' },
    withCredentials: false,
  });
}

/**
 * Anexa interceptors de auth a um client existente. Exportado separadamente // ux-neutrality:allow doc-comment
 * para que `createApiClient` continue puro e testável sem React/Router. // ux-neutrality:allow doc-comment
 */
export function attachAuthInterceptors(
  client: AxiosInstance,
  options: {
    getToken: () => string | null; // ux-neutrality:allow auth-token (JWT, RFC 7519)
    onUnauthorized: () => void;
    /** Banco ativo da sessão (E57.F2) — vira o cabeçalho `X-Banco-Ativo`. */
    getBancoAtivo?: () => string | null;
    /** Chamado quando o servidor recusa o contexto guardado (E57.F2.C03). */
    onBancoAtivoInvalido?: () => void;
  },
): AxiosInstance {
  // ux-neutrality:allow auth-token  (JWT, RFC 7519 — sem string de UI)
  client.interceptors.request.use((req: InternalAxiosRequestConfig) => {
    const jwt = options.getToken();
    if (jwt !== null) {
      req.headers.set('Authorization', `Bearer ${jwt}`);
    }
    // E57.F2 (ADR-0066 §2.1): o cabeçalho PROPÕE o contexto de banco; o
    // servidor confere contra o vínculo antes de aplicar. Sem contexto
    // escolhido, o cabeçalho não vai — e o servidor decide (vínculo único
    // entra direto; vários, nenhum caso de banco aparece até a escolha).
    const banco = options.getBancoAtivo?.() ?? null;
    if (banco !== null) {
      req.headers.set('X-Banco-Ativo', banco);
    }
    return req;
  });
  client.interceptors.response.use(
    (resp) => resp,
    async (err: unknown) => {
      if (axios.isAxiosError(err) && err.response?.status === HTTP_UNAUTHORIZED) {
        options.onUnauthorized();
      }
      // E57.F2.C03: o contexto vive no navegador e o vínculo pode ser removido
      // pelo administrador entre duas requisições. Sem este ramo, TODA leitura
      // da sessão passa a responder 403 e a única saída é sair e entrar de novo
      // — o seletor engole o erro e some, porque a lista de vínculos também
      // falha. Aqui a escolha inválida é descartada e a requisição refeita UMA
      // vez, sem contexto: a sessão volta a enxergar o que é dela.
      if (
        axios.isAxiosError(err) &&
        err.response?.status === HTTP_FORBIDDEN &&
        (err.response.data as { detail?: string } | undefined)?.detail === BANCO_ATIVO_INVALIDO &&
        err.config !== undefined &&
        err.config.headers?.['X-Banco-Ativo'] !== undefined
      ) {
        options.onBancoAtivoInvalido?.();
        const retry = { ...err.config };
        delete (retry.headers as Record<string, unknown>)['X-Banco-Ativo'];
        return client.request(retry);
      }
      return Promise.reject(err);
    },
  );
  return client;
}

export function useApiClient(config?: Partial<ApiClientConfig>): AxiosInstance {
  const getToken = useSessionStore.getState; // ux-neutrality:allow auth-token
  const clearToken = useSessionStore((s) => s.clearToken); // ux-neutrality:allow auth-token
  const setSessionExpired = useSessionStore((s) => s.setSessionExpired);
  const setBancoAtivo = useSessionStore((s) => s.setBancoAtivo);
  // Memoiza pelas chaves primitivas para aceitar `config` inline em callers
  // sem exigir reference stability do objeto wrapper.
  return useMemo(
    () => {
      const client = createApiClient(config);
      return attachAuthInterceptors(client, {
        getToken: () => getToken().token, // ux-neutrality:allow auth-token
        getBancoAtivo: () => getToken().bancoAtivo,
        onBancoAtivoInvalido: () => setBancoAtivo(null),
        // Em vez de navegar direto, sinaliza o modal cooperativo de "sessão expirada".
        // Modal lê este flag e cuida da navegação para /login?next=<rota-atual>.
        onUnauthorized: () => {
          clearToken(); // ux-neutrality:allow auth-token
          setSessionExpired(true);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.baseURL, config?.timeoutMs, clearToken, setSessionExpired, setBancoAtivo],
  );
}
